/// <amd-module name="Artifacts/Npm/services/NpmDataService" />
import { NpmBatchOperationType, NpmPackagesBatchRequest } from "Artifacts/Npm/Contracts/Npm";
import { INpmRestClient } from "Artifacts/Npm/RestClient/Npm";
import { BatchExecutor, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import { getFeedNameWithNewView } from "Artifacts/Root/helpers/FeedHelpers";
// import { IPackage } from "Artifacts/Root/types/IPackage";
// import * as NpmPackageDetails from "Feed/Legacy/Protocols/Npm/NpmPackageDetails";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { MinimalPackageDetails, UpstreamingBehavior } from "Artifacts/Root/types/PackagingShared";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

export interface INpmReadmeContent {
  packageName: string;
  packageVersion: string;
  rawReadmeContent: string;
}

export interface INpmDataService extends IVssService {
  batchSize: number;
  batchDelete(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId?: string,
  ): Promise<void[]>;
  batchPermanentlyDelete(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId?: string,
  ): Promise<void[]>;
  batchPromotePackages(
    feed: IFeed,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void[]>;
  batchRestoreToFeed(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId?: string,
  ): Promise<void[]>;
  getReadmeUnscopedPackage(
    feed: IFeed,
    packageName: string,
    packageVersion: string,
  ): Promise<INpmReadmeContent>;
  getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior>;
  setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void>;
  // updatePackage(
  // 	npmPackageVersionDetails: NpmPackageDetails.PackageVersionDetails,
  // 	feed: IFeed,
  // 	packageName: string,
  // 	packageVersion: string,
  // ): Promise<NpmPackage>;
  updatePackages(
    npmBatchRequest: NpmPackagesBatchRequest,
    feedId: string,
    projectId?: string,
  ): Promise<void>;
}

export class NpmDataService extends VssService implements INpmDataService {
  private _npmReadmeContent: INpmReadmeContent | null = null;

  get NpmRestClient(): INpmRestClient {
    return this.pageContext.getRestClient<INpmRestClient>("Npm-AzureArtifacts.INpmRestClient");
  }

  get batchSize() {
    return 100; // SGV TODO
    // return this.pageContext
    // 	.getService<HubWebPageDataService>("IFeedWebPageDataService")
    // 	.getBatchOperationPageSize();
  }

  public updatePackages(
    npmBatchRequest: NpmPackagesBatchRequest,
    feedId: string,
    projectId?: string,
  ): Promise<void> {
    return this.NpmRestClient.updatePackages(npmBatchRequest, feedId, projectId);
  }

  // public updatePackage(
  // 	npmPackageVersionDetails: NpmPackageDetails.PackageVersionDetails,
  // 	feed: IFeed,
  // 	packageName: string,
  // 	packageVersion: string,
  // ): Promise<NpmPackage> {
  // 	return this.NpmRestClient.updatePackage(
  // 		npmPackageVersionDetails,
  // 		feed.id,
  // 		packageName,
  // 		packageVersion,
  // 		feed.project?.id,
  // 	);
  // }

  public async getReadmeUnscopedPackage(
    feed: IFeed,
    packageName: string,
    packageVersion: string,
  ): Promise<INpmReadmeContent> {
    if (
      this._npmReadmeContent != null &&
      this._npmReadmeContent.packageName === packageName &&
      this._npmReadmeContent.packageVersion === packageVersion
    ) {
      return this._npmReadmeContent;
    }

    const feedId = getFeedNameWithNewView(feed.id, feed.selectedViewId);

    return this.NpmRestClient.getReadmeUnscopedPackage(
      feedId,
      packageName,
      packageVersion,
      feed.project?.id,
    ).then(
      (rawReadmeContent) => {
        this._npmReadmeContent = {
          packageName,
          packageVersion,
          rawReadmeContent,
        };
        return this._npmReadmeContent;
      },
      (err) => {
        this._npmReadmeContent = {
          packageName,
          packageVersion,
          rawReadmeContent: "",
        };
        return this._npmReadmeContent;
      },
    );
  }

  public batchDelete(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId: string,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NpmPackagesBatchRequest>{
          data: null,
          operation: NpmBatchOperationType.Unpublish,
          packages: packageDetails,
        };
        await this.NpmRestClient.updatePackages(batchRequest, feedId, projectId);
      },
    );
  }

  public batchRestoreToFeed(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId?: string,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NpmPackagesBatchRequest>{
          data: null,
          operation: NpmBatchOperationType.RestoreToFeed,
          packages: packageDetails,
        };
        await this.NpmRestClient.updateRecycleBinPackages(batchRequest, feedId, projectId);
      },
    );
  }

  public batchPermanentlyDelete(
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
    projectId?: string,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NpmPackagesBatchRequest>{
          data: null,
          operation: NpmBatchOperationType.PermanentDelete,
          packages: packageDetails,
        };
        await this.NpmRestClient.updateRecycleBinPackages(batchRequest, feedId, projectId);
      },
    );
  }

  public batchPromotePackages(
    feed: IFeed,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void[]> {
    return BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <NpmPackagesBatchRequest>{
          data: { viewId },
          operation: NpmBatchOperationType.Promote,
          packages: packageDetails,
        };
        await this.NpmRestClient.updatePackages(batchRequest, feed.id, feed.project?.id);
      },
    );
  }

  public getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior> {
    return this.NpmRestClient.getUpstreamingBehavior(feedId, packageName, projectId);
  }

  public setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void> {
    return this.NpmRestClient.setUpstreamingBehavior(feedId, packageName, behavior, projectId);
  }
}

Services.add("INpmDataService", { serviceFactory: NpmDataService });
